import {
  Box,
  ButtonPrimaryTextOnly,
  ButtonSecondary,
  Dropdown,
  Flex,
  Grid,
  InputBoxed,
  styled,
} from '@creditas-ui/react';
import { Form, Formik } from 'formik';
import type React from 'react';
import * as yup from 'yup';
import type { Address } from '../../../types';
import { brStates } from '../../../utils/helpers';

const Row = styled(Grid)`
  height: 90px;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: flex-start; 
    height: 100%;
    position: relative;
    
    > div {
      margin-right: 20px;
    }

    > span {
      display: block;
      height: 20px; 
      padding: 2px 5px;
    }
  }
`;

const CancelButton = styled(ButtonSecondary)`
  width: 20%;
`;

const ConfirmButton = styled(ButtonPrimaryTextOnly)`
  width: 30%;
`;

export const EditAddress: React.FC<{
  address: Address;
  onSubmmit: (addresss: Address) => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ address, onSubmmit, onCancel, loading }) => {
  const validationSchema = yup.object().shape({
    street: yup.string().min(5).max(70).required('Informe o endereço'),
    number: yup.string().min(1).max(10).required('Informe o número'),
    neighborhood: yup.string().min(2).max(50).required('Informe o bairro'),
    administrativeAreaLevel2: yup
      .string()
      .min(2)
      .max(50)
      .required('Informe a cidade'),
    administrativeAreaLevel1: yup
      .string()
      .required('Informe o estado')
      .oneOf(brStates),
    zipCode: yup
      .string()
      .required('Informe o CEP')
      .matches(/^\d{5}-?\d{3}$/),
  });

  const states = brStates.map((it) => ({ value: it, text: it }));

  return (
    <Formik
      initialValues={address}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => {
        onSubmmit(values);
        actions.setSubmitting(false);
      }}
    >
      {({ submitForm, values, errors, handleChange, dirty, isValid }) => (
        <Form>
          <Box margin="0 5px 0 25px">
            <Row gridTemplateColumns="5fr 3fr">
              <Box>
                <InputBoxed
                  id="street"
                  label="Endereço"
                  data-testid="address-field"
                  name="street"
                  error={errors.street}
                  onChange={handleChange}
                  value={values.street}
                  crossOrigin={undefined}
                />
              </Box>
              <Box>
                <InputBoxed
                  id="number"
                  label="Número"
                  name="number"
                  error={errors.number}
                  onChange={handleChange}
                  value={values.number || ''}
                  crossOrigin={undefined}
                />
              </Box>
            </Row>
            <Row gridTemplateColumns="4fr 4fr" height="100px">
              <Box>
                <InputBoxed
                  id="neighborhood"
                  label="Bairro"
                  name="neighborhood"
                  error={errors.neighborhood}
                  onChange={handleChange}
                  value={values.neighborhood || ''}
                  crossOrigin={undefined}
                />
              </Box>
              <Box>
                <InputBoxed
                  id="complement"
                  label="Complemento"
                  name="complement"
                  error={errors.complement}
                  onChange={handleChange}
                  value={values.complement || ''}
                  crossOrigin={undefined}
                />
              </Box>
            </Row>
            <Row gridTemplateColumns="4fr 2fr 2fr">
              <Box>
                <InputBoxed
                  id="administrativeAreaLevel2"
                  label="Cidade"
                  name="administrativeAreaLevel2"
                  error={errors.administrativeAreaLevel2}
                  onChange={handleChange}
                  value={values.administrativeAreaLevel2}
                  crossOrigin={undefined}
                />
              </Box>
              <Box>
                <Dropdown
                  id="administrativeAreaLevel1"
                  onChangeOption={(e) => handleChange(e.value)}
                  options={states}
                  tabIndex={0}
                  text="Estado"
                  value={values.administrativeAreaLevel1}
                  error={errors.administrativeAreaLevel1}
                />
              </Box>
              <Box>
                <InputBoxed
                  id="zipCode"
                  label="CEP"
                  name="zipCode"
                  error={errors.zipCode}
                  onChange={handleChange}
                  value={values.zipCode}
                  crossOrigin={undefined}
                />
              </Box>
            </Row>
          </Box>
          <Flex justifyContent="right" margin="25px" marginTop="0px">
            <CancelButton
              data-testid="cancelButton"
              onClick={onCancel}
              size="medium"
              disabled={loading}
            >
              Cancelar
            </CancelButton>
            <ConfirmButton
              data-testid="confirmButton"
              size="medium"
              disabled={!dirty || loading || !isValid}
              loading={loading}
              onClick={submitForm}
              style={{ maxHeight: '66px' }}
              type="button"
            >
              Salvar
            </ConfirmButton>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};
